import { Filesystem, Directory } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { PHOTODROP_DOWNLOAD_TOAST_SCOPE } from '@/store/modules/toast';
import { isNativeNotWeb } from '@/utils/platform-check';

const MAX_PAGINATION_PAGES = 50;
const FETCH_TIMEOUT_MS = 30000;
// Android's Media plugin REQUIRES an album (it rejects saves without albumIdentifier);
// the album is a gallery-visible folder. iOS saves to the Camera Roll with no album.
const ALBUM_NAME = 'Generations Conference';
const isAndroid = () => Capacitor.getPlatform() === 'android';

export default {
  data() {
    return {
      isDownloading: false,
      downloadProgress: 0,
      downloadTotal: 0,
    };
  },

  computed: {
    downloadProgressText() {
      if (!this.isDownloading) return '';
      if (isNativeNotWeb()) {
        return `Saving ${this.downloadProgress} of ${this.downloadTotal}...`;
      }
      return 'Downloading...';
    },
  },

  methods: {
    // Android only: find or create the gallery folder to save into. iOS never calls this.
    async _getOrCreateAlbum() {
      try {
        const { albums } = await Media.getAlbums();
        const existing = albums.find(a => a.name === ALBUM_NAME);
        if (existing) return existing.identifier;
        // createAlbum resolves WITHOUT returning the new album's identifier, so we can't
        // use its result — re-fetch the album list and look the freshly-created one up.
        await Media.createAlbum({ name: ALBUM_NAME });
        const { albums: refreshed } = await Media.getAlbums();
        return refreshed.find(a => a.name === ALBUM_NAME)?.identifier;
      } catch {
        return undefined;
      }
    },

    _isAlbumIdentifierError(error) {
      const msg = (error?.message || error || '').toString().toLowerCase();
      return msg.includes('album') && msg.includes('identifier');
    },

    async _savePhotoToDevice(url, mediaType, albumId) {
      // Debug: simulate random failures. Enable via VUE_APP_DEBUG_DOWNLOAD_FAIL_RATE in .env-local
      const debugFailRate = parseFloat(process.env.VUE_APP_DEBUG_DOWNLOAD_FAIL_RATE);
      if (debugFailRate > 0 && Math.random() < debugFailRate) {
        throw new Error(`[DEBUG] Simulated download failure for ${url}`);
      }

      let fileName;
      try {
        let urlExt = '';
        try {
          urlExt = (new URL(url).pathname.split('.').pop() || '').toLowerCase();
        } catch {
          // non-standard url — leave urlExt empty
        }
        const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', '3gp'];
        // Trust the media type the caller already knows from the CMS item; fall back to
        // the URL extension. (We no longer sniff the HTTP content-type — presigned S3
        // URLs often return application/octet-stream, which mis-saved videos as .jpg.)
        const isVideo =
          mediaType === 'video'
            ? true
            : mediaType === 'image'
              ? false
              : VIDEO_EXTENSIONS.includes(urlExt);

        const ext = isVideo ? 'mp4' : 'jpg';
        const prefix = isVideo ? 'video' : 'photo';
        fileName = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

        // Download natively straight to disk. Do NOT pull the file through the WebView
        // JS heap (the old CapacitorHttp blob → base64 approach): a large video's base64
        // string exceeded iOS's WebContent memory limit and crashed the web process.
        await Filesystem.downloadFile({
          url,
          path: fileName,
          directory: Directory.Cache,
          readTimeout: FETCH_TIMEOUT_MS,
          connectTimeout: FETCH_TIMEOUT_MS,
          progress: true, // emit 'progress' events so the UI can show a real download bar
        });

        const { uri } = await Filesystem.getUri({
          path: fileName,
          directory: Directory.Cache,
        });

        // iOS: no albumIdentifier → saves to the Camera Roll (Photos > Recents), where
        // users look. Android: the plugin requires an album, so pass the folder id.
        const saveMedia = isVideo ? Media.saveVideo : Media.savePhoto;
        const saveOptions = albumId ? { path: uri, albumIdentifier: albumId } : { path: uri };
        try {
          await saveMedia(saveOptions);
        } catch (e) {
          // Android album id can go stale — refresh it once and retry.
          if (albumId && this._isAlbumIdentifierError(e)) {
            const freshAlbumId = await this._getOrCreateAlbum();
            await saveMedia({ path: uri, albumIdentifier: freshAlbumId });
            this._cachedAlbumId = freshAlbumId;
          } else {
            throw e;
          }
        }
      } finally {
        if (fileName) {
          try {
            await Filesystem.deleteFile({ path: fileName, directory: Directory.Cache });
          } catch {
            // already cleaned or never created
          }
        }
      }
    },

    async _silentRetry(failedUrls, albumId) {
      const stillFailed = [];
      for (const url of failedUrls) {
        try {
          await this._savePhotoToDevice(url, undefined, albumId);
        } catch (e) {
          console.error('Silent retry failed:', e);
          stillFailed.push(url);
        }
      }
      return stillFailed;
    },

    // Builds "photo"/"video"/"item" (pluralised) from the saved items, so the success
    // toast reads correctly for a single video, a single photo, or a mixed batch.
    _downloadNoun(items, count) {
      const kinds = new Set((items || []).map(i => (i?.type === 'video' ? 'video' : 'photo')));
      const base = kinds.size === 1 ? [...kinds][0] : 'item';
      return `${base}${count !== 1 ? 's' : ''}`;
    },

    _downloadProgressMessage(items, isRetry) {
      const total = this.downloadTotal || 1;
      const current = Math.min(this.downloadProgress + 1, total);
      if (isRetry) return `Retrying ${current} of ${total}...`;
      if (total === 1) return `Downloading ${this._downloadNoun(items, 1)}...`;
      return `Saving ${current} of ${total}...`;
    },

    // Combines completed-file count with the current file's byte fraction into one
    // overall percentage. `_currentFraction` is null when the server sends no
    // Content-Length; for a single file that means we show an indeterminate bar.
    _updateDownloadProgressToast(items, isRetry) {
      const total = this.downloadTotal || 1;
      const frac = this._currentFraction;
      const value =
        frac === null && total === 1
          ? null
          : Math.min(100, Math.round(((this.downloadProgress + (frac || 0)) / total) * 100));
      this.$store.dispatch(
        'toast/updateProgress',
        { message: this._downloadProgressMessage(items, isRetry), value },
        { root: true },
      );
    },

    // Registers a Filesystem download-progress listener that feeds the toast bar.
    // Returns the listener handle (remove it when the batch finishes).
    async _startProgressTracking(items, isRetry) {
      this._currentFraction = 0;
      this.$store.dispatch(
        'toast/showProgress',
        {
          message: this._downloadProgressMessage(items, isRetry),
          scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE,
          value: 0,
        },
        { root: true },
      );
      return Filesystem.addListener('progress', status => {
        this._currentFraction =
          status.contentLength > 0 ? status.bytes / status.contentLength : null;
        this._updateDownloadProgressToast(items, isRetry);
      });
    },

    _dispatchDownloadResultToast(savedCount, totalCount, items) {
      const failedCount = totalCount - savedCount;
      if (failedCount === 0) {
        this.$store.dispatch(
          'toast/success',
          {
            message: `${savedCount} ${this._downloadNoun(items, savedCount)} saved to Photos`,
            scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE,
          },
          { root: true },
        );
      } else {
        this.$store.dispatch(
          'toast/show',
          {
            message: `${failedCount} ${failedCount !== 1 ? 'downloads' : 'download'} failed.`,
            color: 'bg-[#F59E0B]',
            icon: require('../../public/assets/icons/alert-triangle.svg'),
            scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE,
            action: { label: 'Retry', vuexAction: 'photos/triggerRetry' },
            persistent: true,
          },
          { root: true },
        );
      }
    },

    // selectedPhotos / retry lists hold item.url strings. For videos we must download
    // videoUrl (the actual video), not item.url (the poster image), so resolve each
    // selected url back to its CMS item across both the "For You" and "For everyone" tabs.
    _resolveSelectedForDownload(urls) {
      const photosState = this.$store.state.photos;
      const dayGroups = Array.isArray(photosState.photos) ? photosState.photos : [];
      const everyone = Array.isArray(photosState.everyonePhotos) ? photosState.everyonePhotos : [];
      const allItems = [
        ...everyone,
        ...dayGroups.flatMap(day => (Array.isArray(day.links) ? day.links : [])),
      ];
      const byUrl = new Map(allItems.map(item => [item.url, item]));
      return urls.map(url => {
        const item = byUrl.get(url);
        if (item && item.type === 'video') {
          return { url: item.videoUrl || item.url, type: 'video' };
        }
        return { url, type: item?.type };
      });
    },

    async downloadPhotos(urls, isRetry = false) {
      if (this.isDownloading || !urls.length) return;

      // Entries may be plain URL strings (retry / legacy callers) or { url, type } objects.
      // The type lets _savePhotoToDevice save videos as videos instead of .jpg images.
      const items = urls.map(entry =>
        typeof entry === 'string' ? { url: entry, type: undefined } : entry,
      );

      this.isDownloading = true;
      this.downloadProgress = 0;
      this.downloadTotal = urls.length;
      this._currentFraction = 0;
      this.$store.commit('photos/clearFailedUrls');

      let progressHandle = null;
      try {
        if (isNativeNotWeb()) {
          // Android needs an album to save into; iOS saves to the Camera Roll (no album).
          this._cachedAlbumId = isAndroid() ? await this._getOrCreateAlbum() : undefined;
          progressHandle = await this._startProgressTracking(items, isRetry);

          const failed = [];
          for (const item of items) {
            const url = item?.url;
            if (!url) {
              this.downloadProgress++;
              this._currentFraction = 0;
              this._updateDownloadProgressToast(items, isRetry);
              continue;
            }
            try {
              await this._savePhotoToDevice(url, item.type, this._cachedAlbumId);
            } catch (e) {
              console.error('Failed to save photo:', e);
              failed.push(url);
            }
            this.downloadProgress++;
            this._currentFraction = 0;
            this._updateDownloadProgressToast(items, isRetry);
          }
          const stillFailed = failed.length
            ? await this._silentRetry(failed, this._cachedAlbumId)
            : [];
          if (stillFailed.length) this.$store.commit('photos/setFailedUrls', stillFailed);
          this._dispatchDownloadResultToast(urls.length - stillFailed.length, urls.length, items);
        } else {
          // Web: API returns a direct ZIP URL. presignedUrls must be plain strings —
          // items may be { url, type } objects (see the normalization above).
          const { data: zipUrl } = await axios.post(
            process.env.VUE_APP_API_URL + '/photo/multi-download',
            { presignedUrls: items.map(item => item.url) },
            { headers: { Authorization: `Bearer ${this.$store.state.user.token}` } },
          );
          const link = document.createElement('a');
          link.href = zipUrl;
          link.target = '_blank';
          link.download = 'photos.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (e) {
        console.error('Download failed:', e);
        this.$store.dispatch(
          'toast/error',
          { message: 'Download failed. Please try again.', scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE },
          { root: true },
        );
      } finally {
        if (progressHandle) {
          try {
            await progressHandle.remove();
          } catch {
            // listener already gone
          }
        }
        this.isDownloading = false;
        this.downloadProgress = 0;
        this.downloadTotal = 0;
        this._currentFraction = 0;
      }
    },

    async downloadAllPhotos(tabName, conferenceType) {
      if (this.isDownloading) return;
      this.isDownloading = true;
      this.downloadProgress = 0;
      this._currentFraction = 0;
      this.$store.commit('photos/clearFailedUrls');

      let progressHandle = null;
      try {
        if (isNativeNotWeb()) {
          // Total is unknown until pagination completes — show an indeterminate bar first.
          this.$store.dispatch(
            'toast/showProgress',
            {
              message: 'Preparing download...',
              scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE,
              value: null,
            },
            { root: true },
          );

          // Native: fetch all pages first, then save to device
          let pageCount = 0;
          while (this.$store.state.photos.viewMore && pageCount < MAX_PAGINATION_PAGES) {
            const loadedPhotos = this.$store.state.photos.everyonePhotos;
            const lastPhoto = loadedPhotos[loadedPhotos.length - 1];
            const url = new URL(lastPhoto.url);
            try {
              await this.$store.dispatch('photos/fetchPhotosEveryone', {
                tabName,
                startWith: url.pathname.substring(1),
                conferenceType,
              });
            } catch (e) {
              console.error('Failed to fetch more photos:', e);
              break; // proceed with whatever was fetched so far
            }
            pageCount++;
          }

          const photos = this.$store.state.photos.everyonePhotos;
          // Android needs an album to save into; iOS saves to the Camera Roll (no album).
          this._cachedAlbumId = isAndroid() ? await this._getOrCreateAlbum() : undefined;
          this.downloadTotal = photos.length;
          // Now that the total is known, switch to a real determinate bar.
          progressHandle = await Filesystem.addListener('progress', status => {
            this._currentFraction =
              status.contentLength > 0 ? status.bytes / status.contentLength : null;
            this._updateDownloadProgressToast(photos, false);
          });
          this._updateDownloadProgressToast(photos, false);
          const failed = [];
          for (const photo of photos) {
            const url = photo.type === 'video' ? photo.videoUrl || photo.url : photo.url;
            if (!url) {
              this.downloadProgress++;
              this._currentFraction = 0;
              this._updateDownloadProgressToast(photos, false);
              continue;
            }
            try {
              await this._savePhotoToDevice(url, photo.type, this._cachedAlbumId);
            } catch (e) {
              console.error('Failed to save photo:', e);
              failed.push(url);
            }
            this.downloadProgress++;
            this._currentFraction = 0;
            this._updateDownloadProgressToast(photos, false);
          }
          const stillFailed = failed.length
            ? await this._silentRetry(failed, this._cachedAlbumId)
            : [];
          if (stillFailed.length) this.$store.commit('photos/setFailedUrls', stillFailed);
          this._dispatchDownloadResultToast(
            photos.length - stillFailed.length,
            photos.length,
            photos,
          );
        } else {
          const { data: zipUrl } = await axios.post(
            process.env.VUE_APP_API_URL + '/photo/download-all',
            { tabName, conferenceType },
            { headers: { Authorization: `Bearer ${this.$store.state.user.token}` } },
          );
          const link = document.createElement('a');
          link.href = zipUrl;
          link.target = '_blank';
          link.download = 'photos.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (e) {
        console.error('Download All failed:', e);
        this.$store.dispatch(
          'toast/error',
          { message: 'Download failed. Please try again.', scope: PHOTODROP_DOWNLOAD_TOAST_SCOPE },
          { root: true },
        );
      } finally {
        if (progressHandle) {
          try {
            await progressHandle.remove();
          } catch {
            // listener already gone
          }
        }
        this.isDownloading = false;
        this.downloadProgress = 0;
        this.downloadTotal = 0;
        this._currentFraction = 0;
      }
    },
  },
};
